import { useState } from "react";
import { Link } from "react-router-dom";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/password-reset-request", { email });
      setDone(true);
    } catch (e) {
      // Endpoint is designed to always succeed (no account enumeration).
      // Only surface transport-level errors.
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-lg p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><InfinityMark size={30}/></div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600">Account recovery</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Reset your password</h1>
          </div>
        </div>

        {!done ? (
          <>
            <p className="text-sm text-slate-500 mt-3 mb-6">
              Enter the email address associated with your ConnectMyHub account. A ConnectMyHub admin will send you a secure password-reset link.
            </p>
            <form onSubmit={submit} data-testid="forgot-form">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email address</label>
              <input
                type="email" required autoFocus autoComplete="email"
                data-testid="forgot-email" value={email} onChange={e=>setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none mb-4"
              />
              {err && <div className="mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <button data-testid="forgot-submit" disabled={busy}
                      className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {busy && <Loader2 size={14} className="animate-spin"/>} Send reset link
              </button>
            </form>
          </>
        ) : (
          <div className="mt-5" data-testid="forgot-success">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5"/>
              <div>
                <div className="text-sm font-semibold text-emerald-900">Check your inbox</div>
                <p className="text-xs text-emerald-800 mt-1">
                  If an account exists for that email, a ConnectMyHub admin will send you a secure password-reset link shortly. For security we don't confirm whether an account exists.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-slate-200 text-center">
          <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
            <ArrowLeft size={12}/> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
